import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useSpring } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function About() {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const [profile] = useState({
    bio1: 'I am a Computer Science & Engineering scholar at Brainware University, specializing in Artificial Intelligence and Machine Learning. Based in Kolkata, I operate at the intersection of intelligent systems research, scalable software engineering, and creative visual storytelling.',
    bio2: 'With deep interests spanning deep learning architectures, full-stack systems, and Android development, I approach software with rigorous engineering and aesthetic precision. Outside the compiler, I am an avid photographer, visual creator, and open-source contributor exploring technology from every angle.',
    photoUrl: '/arup.png'
  });

  // ── GSAP ScrollTrigger on section heading ──────────────────────────────
  useEffect(() => {
    if (!titleRef.current) return;
    gsap.fromTo(titleRef.current,
      { y: 16, opacity: 0 },
      {
        y: 0, opacity: 1, duration: 0.35,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: titleRef.current,
          start: 'top 98%',
          once: true,
        },
      }
    );
  }, []);

  // ── Framer Motion scroll parallax on photo ─────────────────────────────
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  const bio1 = profile.bio1;
  const bio2 = profile.bio2;
  const photoUrl = profile.photoUrl;



  const tiltX = useSpring(useMotionValue(0), { stiffness: 100, damping: 30 });
  const tiltY = useSpring(useMotionValue(0), { stiffness: 100, damping: 30 });

  const handleMouseMove = (e) => {
    const rect = sectionRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    tiltX.set(y * -12); // Rotate around X-axis based on Y mouse position
    tiltY.set(x * 12);  // Rotate around Y-axis based on X mouse position
  };

  const handleMouseLeave = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  return (
    <section 
      className="page-section about-section" 
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ background: 'transparent', overflow: 'visible' }}
    >

      <div className="section-inner about-inner" style={{ perspective: '1200px', transformStyle: 'preserve-3d' }}>
        
        {/* Photo — wrapped with Framer Motion for parallax & 3D Tilt */}
        <motion.div 
          className="about-img-wrap" 
          style={{ 
            y: photoY,
            rotateX: tiltX,
            rotateY: tiltY,
            transformStyle: 'preserve-3d'
          }}
        >
          <div
            className="about-photo-gradient-border"
            style={{ transform: 'translateZ(50px)' }}
          >
            <div className="about-photo-inner">
              <img
                src={photoUrl}
                alt="Arup Das"
                className="about-photo-img"
                onError={e => {
                  e.target.onerror = null;
                  e.target.src = '/arup.png';
                }}
              />
            </div>
          </div>
          {/* Backdrop depth glow */}
          <div style={{
            position: 'absolute',
            inset: '-20px',
            background: 'radial-gradient(circle, var(--purple-dim) 0%, transparent 70%)',
            zIndex: -1,
            transform: 'translateZ(-20px)',
            opacity: 0.5
          }} />
        </motion.div>

        {/* Content — Framer Motion whileInView entrance + 3D Tilt */}
        <motion.div
          className="about-content"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          style={{ 
            rotateX: tiltX,
            rotateY: tiltY,
            transformStyle: 'preserve-3d',
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(10px)',
            padding: '40px',
            borderRadius: '24px',
            border: '1px solid rgba(138, 92, 246, 0.2)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.3)'
          }}
        >
          <div style={{ transform: 'translateZ(40px)' }}>
            <span className="section-label">— PERSONAL &amp; PROFESSIONAL PROFILE —</span>
            <h2 className="section-title" ref={titleRef}>About <span>Me</span></h2>
            <div className="section-line" style={{ marginLeft: 0 }} />
            <p className="section-sub about-sub">
              {bio1.includes('Artificial Intelligence')
                ? (<>{bio1.split('Artificial Intelligence and Machine Learning')[0]}<strong style={{ color: 'var(--purple-light)' }}>Artificial Intelligence and Machine Learning</strong>{bio1.split('Artificial Intelligence and Machine Learning')[1]}</>)
                : bio1
              }
              <br /><br />{bio2}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}